<template>
  <div class="orders-grid-actions">
    <BackstageTooltip :text="tooltipText" :key="seconds">
      <BackstageIcon
        v-if="showEditIcon"
        name="edit"
        :class="
          isEditButtonEnabledForDealers
            ? 'orders-grid-actions-edit-order-icon-enabled'
            : 'orders-grid-actions-edit-order-icon-disabled'
        "
        @click="onDealerOrderEditing" />
    </BackstageTooltip>
    <BackstageIcon name="xlsx-file" class="orders-grid-actions-xlsx-download" @click="onXlsxDownload(props.params)" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import useCounter from '@/composables/useCounter';
import { computed, getCurrentInstance, onMounted } from 'vue';
import { useAnalytics } from '@/composables/analytics';
import { OrdersService } from '@/services/orders.service';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageTooltip from '@/components/common/BackstageTooltip.vue';
import { useFormat } from '@/composables/format';
import router, { RouteNames } from '@/router';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import type { Order } from '@/models/order.model';
import { ordersMixins } from '@/mixins/orders.mixins';
import { dealerOrderEditTimeLimit } from '@/constants/order-update.constants';

const props = defineProps<{
  params: any; // Provided by ag-grid // TODO : eventually should add type for params
}>();

const { selectedAccountId: accountId } = storeToRefs(useUserStore());

const { t } = useI18n();
const { addCustomGaEvent } = useAnalytics();
const user = useUserStore();
const format = useFormat();
let showEditIcon = false;

const globalProperties = getCurrentInstance()?.appContext.config.globalProperties;

const onXlsxDownload = async (params: any) => {
  const ordersService = new OrdersService();
  if (params.colDef.field === 'actions') {
    try {
      const fileName = `${params.data.orderNumber} ${new Date().toISOString().slice(0, 10)}.xlsx`;
      const orderNumber = params.data.orderNumber;
      await ordersService.downloadOrder([orderNumber], accountId.value as string, fileName);
    } catch (err) {
      // Handle unexpected errors
      addCustomGaEvent({
        event: GaCustomEvents.FileDownloadError,
        link_text: params.data.orderNumber,
      });
      globalProperties?.$snotify.warning(t('documentsCheckout.document_is_not_available'));
    }
  }
};

const onDealerOrderEditing = () => {
  router.push({
    name: RouteNames.EditOrder,
    params: {
      id: props.params.data.orderNumber,
      editType: props.params.data.type,
      step: AddEditOrderBreadcrumbSteps.productsModelsSelection,
    },
  });
};

const { minutes, seconds } = useCounter(props.params.data.date, dealerOrderEditTimeLimit);

const tooltipText = computed(() => {
  const formattedMinutes = format.formatTimeForTwoDigits(minutes.value);
  const formattedSeconds = format.formatTimeForTwoDigits(seconds.value);
  return t('viewOrders.edit_action_tooltip_title', {
    time: `${formattedMinutes}:${formattedSeconds}`,
  });
});

const isEditButtonEnabledForDealers = computed(() => {
  return (
    user.isDealer() &&
    ordersMixins.isOrderEditableForDealer(props.params.data as Order, user?.userProfile?.user) &&
    (minutes.value > 0 || seconds.value > 0)
  );
});

onMounted(() => {
  showEditIcon = isEditButtonEnabledForDealers.value;
});
</script>

<style lang="scss" scoped>
.orders-grid-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  &-xlsx-download {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }

  &-edit-order-icon {
    width: 14px;
    height: 14px;

    &-enabled {
      cursor: pointer;
    }

    &-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
